import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError, map, switchMap } from 'rxjs';
import {
  LoginCredentials,
  User,
  RegisterUserDto,
  RegisterResponse,
  Rol
} from '../models/user.interface';

// Respuesta real del backend (solo access_token)
interface BackendLoginResponse {
  access_token: string;
}

// Payload decodificado del JWT
interface JwtPayload {
  sub: string;
  position: string;
  email: string;
  is_active: boolean;
  customer_type: string;
  rol_id: number;
  iat: number;
  exp: number;
}
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'comex360_token';
  private readonly USER_KEY = 'comex360_user';
  private readonly API_URL = environment.apiUrl;

  // Signals para estado reactivo
  currentUser = signal<User | null>(null);
  isAuthenticated = signal<boolean>(false);
  isLoading = signal<boolean>(false);

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.loadUserFromStorage();
  }

  /**
   * Carga el usuario desde localStorage al iniciar
   */
  private loadUserFromStorage(): void {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const userStr = localStorage.getItem(this.USER_KEY);

    if (token && userStr) {
      if (this.isTokenExpired(token)) {
        this.clearStorage();
        return;
      }
      try {
        const user = JSON.parse(userStr);
        this.currentUser.set(user);
        this.isAuthenticated.set(true);
      } catch (error) {
        this.clearStorage();
      }
    }
  }

  /**
   * Inicia sesión con credenciales
   * POST /api/auth/login
   * Después de obtener el token, carga los datos completos del usuario usando su ID
   */
  login(credentials: LoginCredentials): Observable<User> {
    this.isLoading.set(true);

    return this.http.post<BackendLoginResponse>(`${this.API_URL}/auth/login`, credentials).pipe(
      tap(response => {
        // Guardar token primero
        localStorage.setItem(this.TOKEN_KEY, response.access_token);
        this.isAuthenticated.set(true);
      }),
      // Decodificar token para obtener el ID del usuario
      switchMap(response => {
        const userId = this.decodeToken(response.access_token);
        if (!userId) {
          throw new Error('No se pudo obtener el ID del usuario del token');
        }
        return this.fetchUserProfile(userId);
      }),
      tap(user => {
        // Guardar usuario completo
        localStorage.setItem(this.USER_KEY, JSON.stringify(user));
        this.currentUser.set(user);
        this.isLoading.set(false);
      }),
      catchError(error => {
        this.isLoading.set(false);
        this.clearStorage();
        return throwError(() => this.handleError(error));
      })
    );
  }

  /**
   * Decodifica el JWT token para obtener el ID del usuario
   */
  private decodeToken(token: string): string | null {
    try {
      const payload = token.split('.')[1];
      const decodedPayload = JSON.parse(atob(payload));
      console.log('Token decodificado:', decodedPayload);

      // El backend usa 'identifier' para el user ID
      const userId = decodedPayload.identifier || decodedPayload.sub || decodedPayload.id;
      console.log('User ID extraído del token:', userId);

      return userId || null;
    } catch (error) {
      console.error('Error decodificando token:', error);
      return null;
    }
  }

  /**
   * Recarga el perfil del usuario actual desde el backend
   */
  public reloadUserProfile(): Observable<User> {
    const currentUser = this.currentUser();
    if (!currentUser?.id) {
      return throwError(() => new Error('No hay usuario autenticado'));
    }

    return this.http.get<User>(`${this.API_URL}/users/${currentUser.id}`).pipe(
      tap(user => {
        console.log('Perfil de usuario recargado:', user);
        // Actualizar localStorage y signal
        localStorage.setItem(this.USER_KEY, JSON.stringify(user));
        this.currentUser.set(user);
      }),
      catchError(error => {
        console.error('Error recargando perfil de usuario:', error);
        throw error;
      })
    );
  }

  /**
   * Obtiene el perfil completo del usuario por ID
   * GET /api/users/{id}
   */
  private fetchUserProfile(userId: string): Observable<User> {
    console.log('Obteniendo perfil de usuario con ID:', userId);
    return this.http.get<User>(`${this.API_URL}/users/${userId}`).pipe(
      tap(user => console.log('Usuario obtenido exitosamente:', user)),
      catchError(error => {
        console.error('Error obteniendo perfil de usuario:', error);
        throw error;
      })
    );
  }

  /**
   * Registra un nuevo usuario
   * POST /api/users
   * El backend retorna directamente el User creado
   */
  register(userData: RegisterUserDto): Observable<User> {
    this.isLoading.set(true);

    return this.http.post<User>(`${this.API_URL}/users`, userData).pipe(
      tap(user => {
        console.log('Usuario registrado:', user);
        this.isLoading.set(false);
      }),
      catchError(error => {
        console.error('Error en registro:', error);
        this.isLoading.set(false);
        return throwError(() => this.handleError(error));
      })
    );
  }

  /**
   * Verifica el email del usuario
   * GET /api/auth/verify-email/{token}/{email}
   */
  verifyEmail(token: string, email: string): Observable<string> {
    return this.http.get(`${this.API_URL}/auth/verify-email/${token}/${email}`, {
      responseType: 'text'
    }).pipe(
      catchError(error => throwError(() => this.handleError(error)))
    );
  }

  /**
   * Re-envía el código de verificación al email del usuario
   * POST /api/auth/resend-verification
   */
  resendVerificationCode(email: string): Observable<any> {
    return this.http.post(`${this.API_URL}/auth/resend-verification`, { email }).pipe(
      catchError(error => throwError(() => this.handleError(error)))
    );
  }

  /**
   * Cambia el email de un usuario durante el registro
   * POST /api/users/change-email
   */
  changeEmail(userId: string, currentEmail: string, newEmail: string): Observable<any> {
    return this.http.post(`${this.API_URL}/users/change-email`, {
      userId,
      currentEmail,
      newEmail
    }).pipe(
      catchError(error => throwError(() => this.handleError(error)))
    );
  }

  /**
   * Envía código OTP para restablecer contraseña
   * POST /api/auth/forgot-password
   */
  sendPasswordResetCode(email: string): Observable<any> {
    return this.http.post(`${this.API_URL}/auth/forgot-password`, { email }).pipe(
      catchError(error => throwError(() => this.handleError(error)))
    );
  }

  /**
   * Verifica el código OTP para restablecimiento de contraseña
   * POST /api/auth/verify-reset-code
   */
  verifyPasswordResetCode(email: string, code: string): Observable<any> {
    return this.http.post(`${this.API_URL}/auth/verify-reset-password-token/${code}`, { email }).pipe(
      catchError(error => throwError(() => this.handleError(error)))
    );
  }

  /**
   * Restablece la contraseña del usuario
   * POST /api/auth/reset-password
   */
  resetPassword(email: string, code: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.API_URL}/auth/reset-password`, {
      email,
      password: newPassword,
      confirm_password: newPassword
    }).pipe(
      catchError(error => throwError(() => this.handleError(error)))
    );
  }

  /**
   * Obtener todos los roles disponibles
   * GET /api/rol
   */
  getRoles(): Observable<Rol[]> {
    return this.http.get<Rol[]>(`${this.API_URL}/rol`).pipe(
      catchError(error => {
        console.error('Error obteniendo roles:', error);
        return throwError(() => this.handleError(error));
      })
    );
  }

  /**
   * Login con redes sociales (Facebook, Twitter, Google)
   */
  loginWithSocial(provider: 'facebook' | 'twitter' | 'google'): void {
    // TODO: Implementar OAuth con el proveedor correspondiente
    console.log(`Login with ${provider} - To be implemented`);
  }

  /**
   * Cierra sesión
   */
  logout(): void {
    this.clearStorage();
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
    this.router.navigate(['/auth/login']);
  }

  /**
   * Guarda la sesión del usuario decodificando el JWT
   */
  private setSession(token: string): User {
    // Decodificar el JWT para obtener el payload
    const payload = this.decodeJwt(token);

    // Construir objeto User desde el payload del JWT
    const user: User = {
      id: '',
      firstName: payload.email.split('@')[0], // Usar parte del email como nombre temporal
      lastName: '',
      email: payload.email,
      position: payload.position as any,
      customer_type: payload.customer_type as any,
      rol_id: payload.rol_id, // Agregar rol_id del payload
      is_verified: payload.is_active
    };

    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.currentUser.set(user);
    this.isAuthenticated.set(true);

    return user;
  }

  /**
   * Decodifica un JWT sin verificar la firma
   */
  private decodeJwt(token: string): JwtPayload {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      throw new Error('Token inválido');
    }
  }

  /**
   * Limpia el almacenamiento local
   */
  private clearStorage(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  /**
   * Obtiene el token actual
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isLoggedIn(): boolean {
    const token = this.getToken();
    if (!token || this.isTokenExpired(token)) {
      if (this.isAuthenticated()) {
        this.clearStorage();
        this.currentUser.set(null);
        this.isAuthenticated.set(false);
      }
      return false;
    }
    return this.isAuthenticated();
  }

  /**
   * Verifica si el token JWT ha expirado
   */
  isTokenExpired(token?: string | null): boolean {
    const t = token ?? this.getToken();
    if (!t) return true;
    try {
      const payload = JSON.parse(atob(t.split('.')[1]));
      if (!payload.exp) return false;
      const expirationDate = payload.exp * 1000;
      return Date.now() >= expirationDate;
    } catch {
      return true;
    }
  }

  /**
   * Obtiene el nombre completo del usuario
   */
  getUserFullName(): string {
    const user = this.currentUser();
    if (user) {
      return `${user.firstName} ${user.lastName}`;
    }
    return '';
  }

  /**
   * Verifica si el usuario tiene un rol específico
   */
  hasRole(role: string): boolean {
    const user = this.currentUser();
    if (!user) return false;
    return user.rol?.name === role || user.position === role;
  }

  /**
   * Obtener tipos de documentos personales requeridos
   * GET /api/type-documents?applicable_to=USER
   */
  getPersonalDocumentTypes(): Observable<any> {
    return this.http.get(`${this.API_URL}/type-documents?applicable_to=USER`).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Subir documentos personales del usuario
   * POST /api/personal-documents/upload
   */
  uploadPersonalDocuments(userId: string, documents: { file: File; type: string; typeId: number }[]): Observable<any> {
    const formData = new FormData();

    // Agregar todos los archivos
    documents.forEach(doc => {
      formData.append('files', doc.file);
    });

    // Agregar user_id y uploader_id
    formData.append('user_id', userId);
    formData.append('uploader_id', userId); // El mismo usuario sube sus documentos

    // Agregar tipos de documentos como elementos individuales del array
    documents.forEach(doc => {
      formData.append('type_documents', doc.type);
    });

    // Obtener token para autenticación
    const token = this.getToken();
    const headers = {
      'Authorization': `Bearer ${token}`
    };

    return this.http.post(`${this.API_URL}/personal-documents/upload`, formData, { headers }).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Verifica si el usuario es OPERATOR_COMPLIANCE u OPERATOR_COMEX360
   */
  isComplianceOperator(): boolean {
    return this.hasRole('OPERATOR_COMPLIANCE') || this.hasRole('OPERATOR_COMEX360');
  }

  /**
   * Maneja errores de la API
   */
  private handleError(error: HttpErrorResponse): string {
    let errorMessage = 'Ha ocurrido un error inesperado';

    if (error.error instanceof ErrorEvent) {
      // Error del cliente
      errorMessage = error.error.message;
    } else {
      // Error del servidor
      let serverError = error.error;

      // Intentar parsear el error si viene como string (ej. por responseType: 'text')
      if (typeof serverError === 'string') {
        try {
          serverError = JSON.parse(serverError);
        } catch (e) {
          // Si no es un JSON válido, conservar el string
        }
      }

      // El backend puede retornar message como string o como array
      if (serverError?.message) {
        if (Array.isArray(serverError.message)) {
          errorMessage = serverError.message.join(', ');
        } else {
          errorMessage = serverError.message;
        }
      } else if (serverError?.response?.message) { // en caso que venga envuelto en un objeto response
        errorMessage = serverError.response.message;
      } else if (error.status === 401) {
        errorMessage = 'Credenciales inválidas';
      } else if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción';
      } else if (error.status === 404) {
        errorMessage = 'Usuario no encontrado';
      } else if (error.status === 400) {
        // Intentar extraer mensaje del error alternativo
        if (typeof serverError === 'string') {
          errorMessage = serverError;
        } else if (Array.isArray(serverError)) {
          errorMessage = serverError.join(', ');
        } else {
          errorMessage = 'Datos inválidos';
        }
      } else if (error.status === 0) {
        errorMessage = 'No se pudo conectar con el servidor';
      }
    }

    return errorMessage;
  }
}
